import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Lock, Globe } from "lucide-react";
import { getPlaylist } from "@/lib/playlists.functions";

export const Route = createFileRoute("/playlists/$id")({
  loader: async ({ params }) => {
    try {
      return await getPlaylist({ data: { id: params.id } });
    } catch { throw notFound(); }
  },
  head: ({ loaderData }) => {
    const p = loaderData?.playlist as { title?: string; description?: string | null } | undefined;
    return {
      meta: [
        { title: p?.title ? `${p.title} · Playlist · KAfrequency` : "Playlist · KAfrequency" },
        { name: "description", content: p?.description || "A KAfrequency activation playlist." },
      ],
    };
  },
  component: PlaylistDetail,
});

function PlaylistDetail() {
  const { playlist, items } = Route.useLoaderData() as {
    playlist: { id: string; title: string; description: string | null; is_public: boolean };
    items: Array<{ position: number; activation: { slug: string; title: string; subtitle: string | null; hero_image_url: string | null; activation_number: number | null } | null }>;
  };
  return (
    <div className="mx-auto max-w-4xl px-4 py-16">
      <Link to="/playlists" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
        <ArrowLeft size={14} /> Playlists
      </Link>
      <div className="mt-6 flex items-center gap-3">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border hairline text-gold">
          {playlist.is_public ? <Globe size={14} /> : <Lock size={14} />}
        </span>
        <h1 className="font-serif text-4xl md:text-5xl">{playlist.title}</h1>
      </div>
      {playlist.description && <p className="mt-3 max-w-xl text-muted-foreground">{playlist.description}</p>}

      <div className="mt-10 grid gap-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No activations added yet.</p>}
        {items.map((it, idx) => it.activation && (
          <Link key={it.activation.slug} to="/activations/$slug" params={{ slug: it.activation.slug }}
            className="flex items-center gap-4 rounded-xl border hairline bg-charcoal p-3 hover:border-gold">
            <span className="w-8 text-center font-serif text-lg text-gold">{idx + 1}</span>
            {it.activation.hero_image_url ? (
              <img src={it.activation.hero_image_url} alt="" className="h-16 w-16 rounded object-cover" loading="lazy" decoding="async" />
            ) : (
              <span className="h-16 w-16 rounded bg-obsidian" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg text-ivory">{it.activation.title}</p>
              {it.activation.subtitle && <p className="truncate text-xs text-muted-foreground">{it.activation.subtitle}</p>}
            </div>
            {it.activation.activation_number && <span className="text-xs text-gold">№ {it.activation.activation_number}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}
